import { useCallback, useEffect, useState } from 'react';
import { ProductInventoryType } from '../../hooks/use-add-product-data';
import { Typography } from '../../../../components/typography/typography';
import { displayMoneyFormat } from '../../../../util/util';
import { Location } from '../../../../clients/quipli-service/location/location';
import Icon from '../../../../components/icons/icon';
import { toast } from '../../../../util/toasts';

export const LocationTable = ({
  partInventory,
  setPartInventory,
  allLocations,
  isSaving,
}: {
  partInventory: ProductInventoryType[];
  setPartInventory: (value: ProductInventoryType, locationId: number) => void;
  allLocations: Location[];
  isSaving: boolean;
}) => {
  const [editingRow, setEditingRow] = useState<number | null>(null);

  useEffect(() => {
    setEditingRow(null);
  }, [partInventory.length]);

  const updatePartInventoryField = useCallback(
    (locationId: number, newData: ProductInventoryType) => {
      setPartInventory(newData, locationId);
    },
    [setPartInventory],
  );

  const maxCols = 3;

  const totalValueOrDash = (value: number) => (editingRow !== null ? '--' : value);

  return (
    <div className="w-full overflow-x-auto max-w-full grid min-w-auto xxl:min-w-[47vw]">
      <table className="pretty-table ml-0 mr-0 md:ml-8 md:mr-8 xxl:ml-0">
        <thead className="max-w-full">
          <tr>
            <th colSpan={maxCols}>
              <Typography type="text-xl" className="table-title" weight="medium">
                Part Inventory
              </Typography>
            </th>
          </tr>
          <tr>
            <th>Location</th>
            <th>Current Stock</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {partInventory.length === 0 && (
            <tr>
              <td colSpan={maxCols}>
                <Typography type="text-md">
                  Select at least one location above to add inventory information for this part.
                </Typography>
              </td>
            </tr>
          )}
          {partInventory.map((inventory, index) => (
            <MemoizedConditionalRow
              setValue={(newData: ProductInventoryType) => updatePartInventoryField(inventory.locationId, newData)}
              key={index}
              row={index}
              inventory={inventory}
              allLocations={allLocations}
              setEditingRow={setEditingRow}
              editingRow={editingRow}
              isSaving={isSaving}
            />
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>All Locations</th>
            <th>{totalValueOrDash(partInventory.reduce((acc, inv) => acc + inv.currentStock, 0))}</th>
            <th>--</th>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const ConditionalRow = ({
  inventory,
  editingRow,
  setEditingRow,
  row,
  setValue,
  isSaving,
  allLocations,
}: {
  inventory: ProductInventoryType;
  editingRow: number | null;
  setEditingRow: (value: React.SetStateAction<number | null>) => void;
  row: number;
  setValue: (newData: ProductInventoryType) => void;
  isSaving: boolean;
  allLocations: Location[];
}) => {
  const [unsavedInputValue, setUnsavedInputValue] = useState<ProductInventoryType>({ ...inventory });

  const updateUnsavedPartInventoryField = useCallback(
    (value: number, field: keyof ProductInventoryType) => {
      setUnsavedInputValue((old) => {
        const newInputValues = { ...old };
        newInputValues[field] = value;
        return newInputValues;
      });
    },
    [setUnsavedInputValue],
  );

  useEffect(() => {
    setUnsavedInputValue(inventory);
  }, [inventory]);

  return (
    <tr>
      <td>{allLocations ? allLocations.find((l) => l.id === inventory.locationId)?.name : inventory.locationId}</td>
      <td>
        <ConditionalInput
          value={unsavedInputValue.currentStock}
          setValue={(value) => updateUnsavedPartInventoryField(value, 'currentStock')}
          editingRow={editingRow}
          row={row}
          isSaving={isSaving}
        />
      </td>
      {/* <td>
        <Typography className="conditional-input" type="text-md">
          {unsavedInputValue.soldStock}
        </Typography>
      </td> */}

      <td>
        {editingRow !== row ? (
          <div
            className="part-inv-edit-button"
            onClick={() => {
              if (editingRow !== null) {
                toast('Please save or cancel your changes before editing another row', 'warning');
              } else {
                setEditingRow(row);
              }
            }}
          >
            <Icon icon="Edit" />
          </div>
        ) : (
          <div className="part-inv-edit-button">
            <Icon
              icon="Check"
              onClick={() => {
                setValue(unsavedInputValue);
                setEditingRow(null);
              }}
            />
            <Icon
              icon="Close"
              onClick={() => {
                setUnsavedInputValue(inventory);
                setEditingRow(null);
              }}
            />
          </div>
        )}
      </td>
    </tr>
  );
};

const ConditionalInput = ({
  editingRow,
  row,
  value,
  setValue,
  isSaving,
  isCurrency = false,
  currency = 'USD',
}: {
  editingRow: number | null;
  row: number;
  value: number;
  setValue: (value: number) => void;
  isSaving: boolean;
  isCurrency?: boolean;
  currency?: string;
}) => {
  return (
    <div className="conditional-input-wrapper">
      {editingRow === row ? (
        <input
          value={value}
          className="conditional-input"
          type="number"
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => setValue(parseFloat(e.target.value))}
          disabled={isSaving}
        />
      ) : (
        <Typography className="conditional-input" type="text-md">
          {isCurrency ? displayMoneyFormat(value, currency) : value}
        </Typography>
      )}
    </div>
  );
};

const MemoizedConditionalRow = ConditionalRow;
